import Bugsnag from '@bugsnag/js'
import { Add } from '@mui/icons-material'
import { Box, Icon, InputAdornment, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { postOvertimePayroll } from 'services/api/modules/payroll/payroll-edit'
import * as yup from 'yup'

const OvertimeModal = ({ children, customOvertime, employeeOvertime, time_id, employee_id }) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
  }
  const [overtimeOption, setOvertimeOption] = useState([])
  const handleCallOvertimeList = () => {
    setOvertimeOption(customOvertime)
  }
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()
  const { mutate: submitOvertime } = useMutation(postOvertimePayroll, {
    onSuccess: () => {
      setIsLoading(false)
      setModalOpen(false)
      queryClient.invalidateQueries('retrieveEmployeeData')
    },
    onError: (error) => {
      setIsLoading(false)
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const onSubmit = async (values) => {
    const { overtime } = values
    let arr = overtime.map((item) => {
      const { overtime_items, ...rest } = item
      const { id: overtime_id } = overtime_items
      return { overtime_id: overtime_id, ...rest }
    })
    let data = { overtime: arr }
    setIsLoading(true)
    submitOvertime({ time_id: time_id, employee_id: employee_id, data: data })
  }
  const validationSchema = yup.object().shape({
    overtime: yup.array().of(
      yup.object().shape({
        overtime_items: yup.object().nullable().required('This field is required'),
        hours: yup
          .number()
          .min(0.01, 'The value must be more than zero')
          .max(99.9, 'The value must not be more than 99.9 hours ')
          .transform((value) => (isNaN(value) ? undefined : value))
          .required('This field is required'),
      })
    ),
  })
  return (
    <>
      <Box onClick={handleOpen}>{children}</Box>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={{
          overtime:
            employeeOvertime?.overtime_items.length === 0
              ? [
                  {
                    overtime_items: null,
                    hours: '',
                  },
                ]
              : employeeOvertime?.overtime_items?.map((item) => ({
                  overtime_items: {
                    id: item.id,
                    title: item.title,
                    rate: item.rate,
                  },
                  hours: item.hours,
                })),
        }}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ handleBlur, handleChange, setFieldValue, touched, errors, handleSubmit }) => {
          return (
            <Form>
              <AdxFormModal
                open={isModalOpen}
                onClose={handleClose}
                handleClose={handleClose}
                title="Add overtime"
                enableFooter
                actionText="Apply overtime"
                handleAction={handleSubmit}
              >
                <Box>
                  <Box>
                    <FieldArray name="overtime">
                      {({ push, remove, form }) => {
                        const { values } = form
                        const { overtime } = values
                        return (
                          <Box
                            sx={{
                              my: 2,
                              border: 2,
                              borderColor: 'gray-line.main',
                              borderRadius: '10px',
                            }}
                          >
                            {overtime.map((_, index) => (
                              <Box
                                key={index}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                columnGap={2}
                                padding={1}
                              >
                                <Box sx={{ width: 0.5 }}>
                                  <Field
                                    name={`overtime.${index}.overtime_items`}
                                    as={AdxDropdown}
                                    onFocus={handleCallOvertimeList}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    options={overtimeOption}
                                    value={values?.overtime[index]?.overtime_items}
                                    renderValue={(val) => <Typography>{val.title}</Typography>}
                                    errorMessage={
                                      <ErrorMessage
                                        name={`overtime.${index}.overtime_items`}
                                        component={AdxErrorMessage}
                                      />
                                    }
                                    isErrorShown={
                                      touched.overtime &&
                                      errors.overtime &&
                                      errors.overtime[index]?.overtime_items &&
                                      touched.overtime[index]?.overtime_items
                                    }
                                  >
                                    {overtimeOption.map((list, id) => {
                                      return (
                                        <AdxMenuItem
                                          selectedvalue={values?.overtime[index]?.overtime_items}
                                          key={id}
                                          value={list}
                                        >
                                          {list.title}
                                        </AdxMenuItem>
                                      )
                                    })}
                                  </Field>
                                </Box>
                                <Box display="flex" alignItems="flex-start" columnGap={1}>
                                  <Box sx={{ width: 0.5 }}>
                                    <Field
                                      name={`overtime.${index}.hours`}
                                      type="number"
                                      as={AdxInput}
                                      endAdornment={
                                        <InputAdornment position="end">hour(s)</InputAdornment>
                                      }
                                      onChange={(e) => {
                                        setFieldValue(
                                          `overtime.${index}.hours`,
                                          parseFloat(e.target.value)
                                        )
                                      }}
                                      onBlur={handleBlur}
                                      errorMessage={
                                        <ErrorMessage
                                          name={`overtime.${index}.hours`}
                                          component={AdxErrorMessage}
                                        />
                                      }
                                      isErrorShown={
                                        touched.overtime &&
                                        errors.overtime &&
                                        errors.overtime[index]?.hours &&
                                        touched.overtime[index]?.hours
                                      }
                                    />
                                  </Box>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    width={0.5}
                                    justifyContent="center"
                                    height="45px"
                                  >
                                    {values.overtime[index]?.overtime_items?.rate === undefined ? (
                                      <Typography color="light-gray-text.main">{`(0x hourly salary)`}</Typography>
                                    ) : (
                                      <Typography color="light-gray-text.main">{`(${values.overtime[index]?.overtime_items?.rate}x hourly salary)`}</Typography>
                                    )}
                                  </Box>
                                  <Box display="flex" alignItems="center" height="45px">
                                    <Icon
                                      sx={{
                                        color: 'error.main',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => remove(index)}
                                    >
                                      remove_circle
                                    </Icon>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            <Box
                              sx={{
                                borderTop: 'solid',
                                borderColor: 'gray-line.main',
                                p: 0.5,
                              }}
                            >
                              <AdxButton
                                variant={'ghost'}
                                onClick={() => push({ overtime_items: null, hours: '' })}
                                startIcon={<Add />}
                                sx={{
                                  textDecoration: 'underline',
                                  width: '100%',
                                  pl: 2,
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                Add overtime
                              </AdxButton>
                            </Box>
                          </Box>
                        )
                      }}
                    </FieldArray>
                  </Box>
                  {/* <Box display="flex" justifyContent="center" columnGap={1} sx={{ mb: 2, mt: 4 }}>
                    <AdxButton
                      variant={'ghost'}
                      onClick={handleClose}
                      sx={{ width: 'calc(100% * 1 / 3)' }}
                    >
                      Cancel
                    </AdxButton>
                    <AdxButton
                      variant={'contained'}
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      disabled={isLoading}
                      sx={{ width: 'calc(100% * 2 / 3)' }}
                    >
                      Apply overtime
                    </AdxButton>
                  </Box> */}
                </Box>
              </AdxFormModal>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default OvertimeModal
